import React, { useState } from "react";
import {
  BtnContainer,
  Container,
  ContentContainer,
  ImageContainer,
  InfoContainer,
  InfoSection,
} from "./RoomManageCard.styles";
import UI from "@mile-meeting/mile-ui";
import { RoomManageCardProps } from "./types";
import RoomDetailInfoModal from "../RoomDetailInfoModal/RoomDetailInfoModal";
import { useToast } from "hook/useToast";

const RoomManageCard: React.FC<RoomManageCardProps> = ({ room, onDelete, onEdit, isEditable, isBlocked = false }) => {
  const { showToast } = useToast();
  const { name, capacity, isInsideTabletUsed, isOutsideTabletUsed, building, floor, image, meetingRoomIntegration } =
    room;
  const [openRoomInfoModal, setOpenRoomInfoModal] = useState<boolean>(false);

  const handleEdit = () => {
    if (isBlocked) {
      openPlanWarningToast();
      return;
    } else if (onEdit) {
      onEdit();
    }
  };

  const openPlanWarningToast = () => {
    showToast("회의실 이용 제한을 해제하시려면 서비스 플랜 변경이 필요합니다.", "warning");
  };

  const handleDelete = () => {
    onDelete?.();
  };

  const renderBuildingFloor = () => {
    if (building?.name && floor?.name) {
      return `${building.name} ${floor.name}`;
    }
    if (!building?.name && floor?.name) {
      return `건물 미설정 / ${floor.name}`;
    }
    if (building?.name && !floor?.name) {
      return `${building.name} / 층 미설정`;
    }
    return "건물/층 미설정";
  };

  //* INFO: 태블릿 사용 여부에 따라 태그 텍스트를 반환하는 함수
  const getTagText = () => {
    if( isInsideTabletUsed && isOutsideTabletUsed) {
      return "내/외부 태블릿 사용";
    } else if (isInsideTabletUsed) {
      return "내부 태블릿 사용";
    } else if (isOutsideTabletUsed) {
      return "외부 태블릿 사용";
    }
    return "태블릿 미사용";
  }

  return (
    <Container isBlocked={isBlocked}>
      <ImageContainer image={image} isBlocked={isBlocked}>
        {!image && <UI.Icon name="image" size={24} color="var(--alias-color-label-assistive)" />}
      </ImageContainer>
      <InfoContainer>
        <ContentContainer>
          <InfoSection>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <UI.Tag
                label={getTagText()}
                type="primary"
                color={isInsideTabletUsed || isOutsideTabletUsed ? "blue" : "gray"}
                size="small"
              />
              <UI.IconButton.Normal
                icon="dotHorizontalCircle"
                size={20}
                color="var(--alias-color-label-assistive)"
                disabled={isBlocked}
                onClick={() => {
                  if (isBlocked) {
                    openPlanWarningToast();
                    return;
                  }
                  setOpenRoomInfoModal(true);
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 4, width: "100%", alignItems: "center" }}>
              <UI.Typography
                type="heading02"
                fontStyle="bold"
                color={isBlocked ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-normal)"}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </UI.Typography>
              {meetingRoomIntegration?.integrationProvider === "GOOGLE" && <UI.Icon name="google" size={20} />}
            </div>
          </InfoSection>
          <InfoSection>
            <div style={{ display: "flex", gap: 4, width: "100%", alignItems: "center" }}>
              <UI.Icon name="office" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography
                type="label01"
                color={isBlocked ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-neutral)"}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {renderBuildingFloor()}
              </UI.Typography>
            </div>
            <div style={{ display: "flex", gap: 4, width: "100%", alignItems: "center" }}>
              <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography
                type="label01"
                color={isBlocked ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-neutral)"}
              >
                {capacity}인
              </UI.Typography>
            </div>
          </InfoSection>
        </ContentContainer>
        {isEditable && (
          <BtnContainer>
            <UI.Button title="수정" primary={false} onClick={handleEdit} disabled={isBlocked} size="small" />
            <UI.Button
              type="outlined"
              title="삭제"
              primary={false}
              onClick={handleDelete}
              style={{ background: "white" }}
              size="small"
            />
          </BtnContainer>
        )}
      </InfoContainer>
      <RoomDetailInfoModal
        onEdit={() => {
          setOpenRoomInfoModal(false);
          onEdit?.();
        }}
        onDelete={() => {
          onDelete?.();
          setOpenRoomInfoModal(false);
        }}
        modalIsOpen={openRoomInfoModal}
        closeModal={() => setOpenRoomInfoModal(false)}
        room={room}
        hasSuperAdminRoles={isEditable}
      />
    </Container>
  );
};

export default React.memo(RoomManageCard);
